import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  PaidOutlined as PayIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import api from '../../services/api';
import service, {
  CategoriaEmbaixador,
  Embaixador,
  EmbaixadorPayload,
  EmbaixadorStats,
  EstadoEmbaixador,
  TierEmbaixador,
} from '../../services/marketing-embaixadores.service';
import { PageHeader, StatCard, FilterBar, EmptyState } from '../../components/Marketing';
import {
  StarsOutlined,
  GroupsOutlined,
  CheckCircleOutline,
  BlockOutlined,
  CalendarMonthOutlined,
} from '@mui/icons-material';
import { todayCv } from '../../utils/date';

const CAT_LABELS: Record<CategoriaEmbaixador, string> = {
  estudante: 'Estudante',
  empreendedor: 'Empreendedor',
  criador_conteudo: 'Criador de Conteúdo',
  outro: 'Outro',
};

const TIER_COLORS: Record<TierEmbaixador, 'default' | 'primary' | 'warning'> = {
  bronze: 'default',
  prata: 'primary',
  ouro: 'warning',
};

const ESTADO_COLORS: Record<EstadoEmbaixador, 'success' | 'default' | 'error'> = {
  ativo: 'success',
  inativo: 'default',
  suspenso: 'error',
};

const EMPTY: EmbaixadorPayload = {
  user_id: '',
  categoria: 'criador_conteudo',
  tier: 'bronze',
  estado: 'ativo',
  pontos_mensais: 100,
  early_access: true,
  badge_icone: '⭐',
};

const formatDate = (v?: string | null) => (v ? new Date(v).toLocaleDateString('pt-PT', { timeZone: 'UTC' }) : '—');
const currentMonth = () => todayCv().slice(0, 7); // mês corrente em CV

export default function Embaixadores() {
  const [rows, setRows] = useState<Embaixador[]>([]);
  const [stats, setStats] = useState<EmbaixadorStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState<{ msg: string; severity: 'success' | 'error' | 'info' } | null>(null);
  const [search, setSearch] = useState('');
  const [categoriaF, setCategoriaF] = useState('');
  const [estadoF, setEstadoF] = useState('');

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<Embaixador | null>(null);
  const [form, setForm] = useState<EmbaixadorPayload>(EMPTY);
  const [saving, setSaving] = useState(false);

  const [processOpen, setProcessOpen] = useState(false);
  const [periodo, setPeriodo] = useState(currentMonth());
  const [processing, setProcessing] = useState(false);

  // User search autocomplete
  interface UserOption { id: string; label: string; email: string }
  const [userOptions, setUserOptions] = useState<UserOption[]>([]);
  const [userSearchLoading, setUserSearchLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserOption | null>(null);
  const userSearchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const searchUsers = (q: string) => {
    if (userSearchTimer.current) clearTimeout(userSearchTimer.current);
    if (!q.trim()) { setUserOptions([]); return; }
    userSearchTimer.current = setTimeout(async () => {
      setUserSearchLoading(true);
      try {
        const res = await api.get(`/admin/users?search=${encodeURIComponent(q)}&limit=10`);
        const list = (res.data.users || res.data.data || []) as any[];
        setUserOptions(list.map((u: any) => ({
          id: String(u.id || u.ID),
          label: u.name || u.nome || u.username || u.USERNAME || u.email || u.EMAIL,
          email: u.email || u.EMAIL || '',
        })));
      } catch {
        setUserOptions([]);
      } finally {
        setUserSearchLoading(false);
      }
    }, 300);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [list, s] = await Promise.all([
        service.list({ categoria: categoriaF, estado: estadoF, search }),
        service.stats().catch(() => null),
      ]);
      setRows(list);
      setStats(s);
    } catch (e: any) {
      setToast({ msg: e?.message || 'Erro', severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [categoriaF, estadoF, search]);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY);
    setSelectedUser(null);
    setUserOptions([]);
    setDialogOpen(true);
  };

  const openEdit = (e: Embaixador) => {
    setEditing(e);
    setForm({
      categoria: e.categoria,
      tier: e.tier,
      estado: e.estado,
      data_inicio: e.data_inicio?.slice(0, 10) || '',
      data_fim: e.data_fim?.slice(0, 10) || '',
      pontos_mensais: e.pontos_mensais,
      early_access: e.early_access === 1,
      badge_icone: e.badge_icone,
      notas: e.notas || '',
    });
    setDialogOpen(true);
  };

  const save = async () => {
    if (!editing && !form.user_id?.trim()) {
      setToast({ msg: 'user_id obrigatório', severity: 'error' });
      return;
    }
    setSaving(true);
    try {
      if (editing) await service.update(editing.id, form);
      else await service.create(form);
      setToast({ msg: editing ? 'Atualizado' : 'Criado', severity: 'success' });
      setDialogOpen(false);
      await load();
    } catch (e: any) {
      setToast({ msg: e?.response?.data?.error || e?.message || 'Erro', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const remove = async (e: Embaixador) => {
    if (!window.confirm(`Remover "${e.nome}" como embaixador?`)) return;
    try {
      await service.remove(e.id);
      setToast({ msg: 'Removido', severity: 'success' });
      await load();
    } catch (ex: any) {
      setToast({ msg: ex?.message || 'Erro', severity: 'error' });
    }
  };

  const processMonthly = async () => {
    setProcessing(true);
    try {
      const r = await service.processMonthly(periodo);
      setToast({
        msg: `${r.processados} créditos processados (${r.total_pontos_creditados} pts total). ${r.ja_processados_antes} já tinham sido pagos.`,
        severity: 'success',
      });
      setProcessOpen(false);
      await load();
    } catch (e: any) {
      setToast({ msg: e?.message || 'Erro', severity: 'error' });
    } finally {
      setProcessing(false);
    }
  };

  return (
    <Box>
      <PageHeader
        title="Embaixadores Pagali"
        subtitle="Badge + pontos mensais + acesso antecipado para criadores, estudantes e empreendedores"
        icon={<StarsOutlined />}
        actions={
          <>
            <Button startIcon={<RefreshIcon />} onClick={load}>Atualizar</Button>
            <Button variant="outlined" startIcon={<PayIcon />} onClick={() => setProcessOpen(true)}>
              Processar créditos
            </Button>
            <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>Novo Embaixador</Button>
          </>
        }
      />

      <Alert severity="info" sx={{ mb: 2 }}>
        Embaixadores recebem <b>pontos mensais</b> configurados por cada um, badge visível na app e
        acesso antecipado a features (Passo 13 do plano). O crédito mensal é idempotente — pode ser
        corrido várias vezes no mesmo período sem duplicar.
      </Alert>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 3, mb: 3 }}>
        <StatCard icon={<GroupsOutlined />} label="Total" value={stats?.total ?? '—'} color="primary" />
        <StatCard icon={<CheckCircleOutline />} label="Ativos" value={stats?.ativos ?? '—'} color="success" />
        <StatCard icon={<BlockOutlined />} label="Suspensos" value={stats?.suspensos ?? '—'} color="error" />
        <StatCard
          icon={<CalendarMonthOutlined />}
          label="Pontos creditados (mês)"
          value={stats?.pontos_mes_atual?.toLocaleString('pt-PT') ?? '—'}
          color="warning"
        />
      </Box>

      <FilterBar>
        <TextField
          size="small"
          label="Pesquisar por nome/email"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <FormControl size="small">
          <InputLabel>Categoria</InputLabel>
          <Select label="Categoria" value={categoriaF} onChange={(e) => setCategoriaF(e.target.value)}>
            <MenuItem value="">Todas</MenuItem>
            {Object.entries(CAT_LABELS).map(([k, v]) => (
              <MenuItem key={k} value={k}>{v}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small">
          <InputLabel>Estado</InputLabel>
          <Select label="Estado" value={estadoF} onChange={(e) => setEstadoF(e.target.value)}>
            <MenuItem value="">Todos</MenuItem>
            <MenuItem value="ativo">Ativo</MenuItem>
            <MenuItem value="inativo">Inativo</MenuItem>
            <MenuItem value="suspenso">Suspenso</MenuItem>
          </Select>
        </FormControl>
      </FilterBar>

      <Paper>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Embaixador</TableCell>
                <TableCell>Categoria</TableCell>
                <TableCell>Tier</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell align="right">Pts/mês</TableCell>
                <TableCell align="right">Histórico</TableCell>
                <TableCell>Período</TableCell>
                <TableCell align="right">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={28} />
                  </TableCell>
                </TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} sx={{ border: 0 }}>
                    <EmptyState
                      icon={<StarsOutlined />}
                      title="Sem embaixadores"
                      description="Promova utilizadores (estudantes, criadores de conteúdo, empreendedores) para receberem pontos mensais e acesso antecipado."
                      action={
                        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
                          Novo Embaixador
                        </Button>
                      }
                    />
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((e) => (
                  <TableRow key={e.id} hover>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="h6">{e.badge_icone}</Typography>
                        <Box>
                          <Typography variant="body2" fontWeight={500}>{e.nome}</Typography>
                          <Typography variant="caption" color="text.secondary">{e.email}</Typography>
                        </Box>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={CAT_LABELS[e.categoria]} variant="outlined" />
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={e.tier.toUpperCase()} color={TIER_COLORS[e.tier]} />
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={e.estado} color={ESTADO_COLORS[e.estado]} />
                    </TableCell>
                    <TableCell align="right">{e.pontos_mensais}</TableCell>
                    <TableCell align="right">
                      <Typography variant="caption">
                        {e.meses_pagos || 0} meses · {(e.pontos_acumulados || 0).toLocaleString('pt-PT')} pts
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">
                        {formatDate(e.data_inicio)} → {formatDate(e.data_fim)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Editar">
                        <IconButton size="small" onClick={() => openEdit(e)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Remover">
                        <IconButton size="small" color="error" onClick={() => remove(e)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Create/Edit */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>{editing ? `Editar: ${editing.nome}` : 'Novo Embaixador'}</DialogTitle>
        <DialogContent dividers sx={{ py: 3 }}>
          <Stack spacing={3}>
            {!editing && (
              <Autocomplete
                options={userOptions}
                value={selectedUser}
                loading={userSearchLoading}
                isOptionEqualToValue={(a, b) => a.id === b.id}
                getOptionLabel={(opt) => `${opt.label} (${opt.email})`}
                onInputChange={(_, val) => searchUsers(val)}
                onChange={(_, val) => {
                  setSelectedUser(val);
                  setForm({ ...form, user_id: val?.id || '' });
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Utilizador"
                    placeholder="Pesquisar por nome ou email…"
                    helperText={selectedUser ? `ID: ${selectedUser.id}` : 'Escreva para pesquisar utilizadores'}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {userSearchLoading ? <CircularProgress size={16} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                noOptionsText="Sem resultados — escreva para pesquisar"
                filterOptions={(x) => x}
              />
            )}

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: 3 }}>
              <FormControl fullWidth>
                <InputLabel>Categoria</InputLabel>
                <Select
                  label="Categoria"
                  value={form.categoria || 'outro'}
                  onChange={(e) => setForm({ ...form, categoria: e.target.value as CategoriaEmbaixador })}
                >
                  {Object.entries(CAT_LABELS).map(([k, v]) => (
                    <MenuItem key={k} value={k}>{v}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>Tier</InputLabel>
                <Select
                  label="Tier"
                  value={form.tier || 'bronze'}
                  onChange={(e) => setForm({ ...form, tier: e.target.value as TierEmbaixador })}
                >
                  <MenuItem value="bronze">Bronze</MenuItem>
                  <MenuItem value="prata">Prata</MenuItem>
                  <MenuItem value="ouro">Ouro</MenuItem>
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>Estado</InputLabel>
                <Select
                  label="Estado"
                  value={form.estado || 'ativo'}
                  onChange={(e) => setForm({ ...form, estado: e.target.value as EstadoEmbaixador })}
                >
                  <MenuItem value="ativo">Ativo</MenuItem>
                  <MenuItem value="inativo">Inativo</MenuItem>
                  <MenuItem value="suspenso">Suspenso</MenuItem>
                </Select>
              </FormControl>
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
              <TextField
                fullWidth
                type="date"
                label="Início"
                InputLabelProps={{ shrink: true }}
                value={form.data_inicio || ''}
                onChange={(e) => setForm({ ...form, data_inicio: e.target.value })}
              />
              <TextField
                fullWidth
                type="date"
                label="Fim (opcional)"
                InputLabelProps={{ shrink: true }}
                value={form.data_fim || ''}
                onChange={(e) => setForm({ ...form, data_fim: e.target.value })}
              />
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr auto' }, gap: 3, alignItems: 'center' }}>
              <TextField
                fullWidth
                type="number"
                label="Pontos mensais"
                value={form.pontos_mensais ?? 100}
                onChange={(e) => setForm({ ...form, pontos_mensais: Number(e.target.value) })}
              />
              <TextField
                fullWidth
                label="Badge (emoji)"
                value={form.badge_icone || '⭐'}
                onChange={(e) => setForm({ ...form, badge_icone: e.target.value })}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={form.early_access !== false}
                    onChange={(e) => setForm({ ...form, early_access: e.target.checked })}
                  />
                }
                label="Acesso antecipado"
              />
            </Box>

            <TextField
              fullWidth
              multiline
              minRows={3}
              label="Notas internas"
              placeholder="Contexto, canais que usa, observações"
              value={form.notas || ''}
              onChange={(e) => setForm({ ...form, notas: e.target.value })}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={save} disabled={saving}>
            {saving ? 'A guardar…' : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Process monthly */}
      <Dialog open={processOpen} onClose={() => setProcessOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Processar créditos mensais</DialogTitle>
        <DialogContent dividers>
          <TextField
            fullWidth
            label="Período"
            value={periodo}
            onChange={(e) => setPeriodo(e.target.value)}
            helperText="Formato YYYY-MM. Idempotente — embaixadores já pagos são ignorados."
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setProcessOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={processMonthly} disabled={processing}>
            {processing ? 'A processar…' : 'Processar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!toast} autoHideDuration={5000} onClose={() => setToast(null)}>
        {toast ? (
          <Alert severity={toast.severity} onClose={() => setToast(null)}>
            {toast.msg}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
}
